<template>
  <!--
    Rechte Seite, wenn links eine besetzte Rolle angeklickt wurde
    (Kundenfeedback 24.10.2026, XIX): „Wenn man bei den grün markierten
    draufklickt, erscheint auf der rechten Seite eben genau dieser Kunde, die
    man bearbeiten oder ändern kann oder noch einen hinzufügen.“
    Je Beteiligtem Organisation (Person als Zusatz), Kontakt, Status und die
    möglichen Aktionen; unten „Weitere … hinzufügen“ bzw. „Austauschen“.
  -->
  <section class="rdp" :aria-labelledby="'rdp-title-' + group.key" data-testid="apd-role-detail">
    <header class="rdp__head">
      <h4 :id="'rdp-title-' + group.key" class="rdp__title">{{ group.label }}</h4>
      <span class="rdp__count">{{ countText }}</span>
    </header>

    <ul class="rdp__list">
      <li v-for="m in group.members" :key="group.key + '-' + m.id" class="rdp__item" :data-testid="'apd-role-member-' + m.id">
        <div class="rdp__text">
          <span class="rdp__name">{{ m.name }}</span>
          <span v-if="m.person" class="rdp__person">{{ m.person }}</span>
          <span class="rdp__meta">
            <span v-if="m.participant.email">{{ m.participant.email }}</span>
            <span v-if="m.statusText" class="rdp__status" :class="{ 'rdp__status--sep': !!m.participant.email }">{{ m.statusText }}</span>
          </span>
        </div>
        <div class="rdp__actions">
          <button
            v-if="canInvite(m)"
            type="button"
            class="rdp__btn"
            :disabled="busy"
            :title="$gt('Einladung per E-Mail senden – danach kann der Beteiligte die Akte in KARS sehen')"
            data-testid="apd-role-invite"
            @click="$emit('invite', m)"
          >{{ $gt('Zur Akte einladen') }}</button>
          <button
            v-if="m.canRemove"
            type="button"
            class="rdp__btn rdp__btn--quiet"
            :disabled="busy"
            :title="$gt('Aus der Akte entfernen – erfasste Daten bleiben erhalten')"
            data-testid="apd-role-remove"
            @click="$emit('remove', m.participant)"
          >{{ $gt('Entfernen') }}</button>
        </div>
      </li>
      <li v-if="group.extraCount > 0" class="rdp__item rdp__item--auto">
        <span class="rdp__person">
          {{ group.extraCount }} {{ group.extraCount === 1 ? $gt('Versicherung aus den Falldaten erkannt (Fahrzeug- bzw. Gegnerangaben)') : $gt('Versicherungen aus den Falldaten erkannt (Fahrzeug- bzw. Gegnerangaben)') }}
        </span>
      </li>
    </ul>

    <p v-if="isMandant" class="rdp__note">
      {{ $gt('Name und Anschrift des Kunden ändern Sie in der Akte unter „Beteiligte“ beim Kunden.') }}
    </p>

    <footer class="rdp__foot">
      <button type="button" class="rdp__add" data-testid="apd-role-add" @click="$emit('add', group.key)">
        <Plus :size="15" aria-hidden="true" />
        {{ $gt('Weitere hinzufügen') }}
      </button>
      <button
        v-if="!isMandant && group.members.length"
        type="button"
        class="rdp__btn"
        :title="$gt('Einen anderen Kontakt für diese Rolle suchen; den bisherigen entfernen Sie anschließend hier.')"
        data-testid="apd-role-replace"
        @click="$emit('add', group.key)"
      >{{ $gt('Austauschen') }}</button>
    </footer>
  </section>
</template>

<script>
import { Plus } from "lucide-vue-next";
import { MANDANT_ROLE_KEY, canInviteMember } from "./participantDialogModel";

export default {
  name: "RoleDetailPanel",

  components: { Plus },

  props: {
    /** Eine Gruppe aus buildRoleColumn() */
    group: { type: Object, required: true },
    busy: { type: Boolean, default: false },
  },

  emits: ["add", "remove", "invite"],

  computed: {
    isMandant() { return this.group.key === MANDANT_ROLE_KEY; },
    countText() {
      const n = this.group.members.length;
      return n === 1 ? this.$gt("1 zugeordnet") : `${n} ${this.$gt("zugeordnet")}`;
    },
  },

  methods: {
    canInvite(member) { return canInviteMember(member, this.group.key); },
  },
};
</script>

<style scoped>
.rdp { display: flex; flex-direction: column; gap: 12px; }
.rdp__head { display: flex; align-items: baseline; gap: 10px; }
.rdp__title { margin: 0; font-size: 15px; font-weight: 700; color: var(--dark-color); }
.rdp__count { font-size: 12px; color: var(--grey-color); }

.rdp__list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.rdp__item {
  display: flex; align-items: center; gap: 12px; padding: 10px 12px;
  border: 1px solid var(--border-grey-color-light); border-radius: 10px; background: #fff;
}
.rdp__item--auto { border-style: dashed; }
.rdp__text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.rdp__name { font-size: 13.5px; font-weight: 650; color: var(--dark-color); overflow-wrap: anywhere; }
.rdp__person { font-size: 12.5px; color: var(--dark-color); }
.rdp__meta { display: flex; flex-wrap: wrap; gap: 4px 10px; font-size: 12px; color: var(--grey-color); }
.rdp__status--sep::before { content: "·"; margin-right: 10px; color: var(--disabled-grey-color); }

.rdp__actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 6px; flex-shrink: 0; }
.rdp__btn {
  height: 30px; padding: 0 12px; border: 1px solid var(--border-grey-color); border-radius: 8px; background: #fff;
  font: inherit; font-size: 12.5px; font-weight: 600; color: var(--dark-color); cursor: pointer; white-space: nowrap;
}
.rdp__btn:hover:not(:disabled) { border-color: var(--primary-color); color: var(--primary-color); }
.rdp__btn--quiet { color: var(--grey-color); }
.rdp__btn:disabled { opacity: .5; cursor: not-allowed; }

.rdp__note { margin: 0; font-size: 12.5px; color: var(--grey-color); }

.rdp__foot { display: flex; flex-wrap: wrap; gap: 8px; padding-top: 4px; }
.rdp__add {
  display: inline-flex; align-items: center; gap: 6px; height: 34px; padding: 0 14px;
  border: 1px solid var(--primary-color); border-radius: 9px; background: #fff;
  font: inherit; font-size: 12.5px; font-weight: 600; color: var(--primary-color); cursor: pointer;
}
.rdp__add:hover { background: var(--primary-light-color-transparent); }
.rdp__btn:focus-visible, .rdp__add:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 2px; }
</style>
